// src/components/contacts/ContactHistory.jsx
import React, { useState, useEffect } from 'react';
import { 
  FiX, FiMail, FiMessageSquare, FiCalendar, FiUser, 
  FiExternalLink, FiEye, FiRefreshCw 
} from 'react-icons/fi';
import { contactsService } from '../../utils/contactsUtils';
import { useAuth } from '../../context/AuthContext';

const ContactHistory = ({ contacts, onClose }) => {
  const { userProfile } = useAuth();
  const organizationId = userProfile?.organizationId;
  
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedContact, setSelectedContact] = useState('all');
  const [selectedType, setSelectedType] = useState('all');

  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    try {
      setLoading(true);
      const contactIds = contacts.map(c => c.id);
      const historyData = await contactsService.getContactHistory(organizationId, contactIds);
      setHistory(historyData);
    } catch (err) {
      setError('Failed to load contact history');
      console.error('Error fetching contact history:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredHistory = history.filter(item => {
    if (selectedContact !== 'all' && !item.contactIds.includes(selectedContact)) {
      return false;
    }
    if (selectedType !== 'all' && item.type !== selectedType) {
      return false;
    }
    return true;
  });

  const getTypeIcon = (type) => {
    switch (type) {
      case 'email': return <FiMail className="text-blue-600" size={16} />;
      case 'linkedin': return <FiMessageSquare className="text-blue-600" size={16} />;
      default: return <FiUser className="text-gray-600" size={16} />;
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'email': return 'Email';
      case 'linkedin': return 'LinkedIn Message';
      default: return 'Unknown';
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Unknown date';
    const date = new Date(timestamp);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const getContactNames = (contactIds) => {
    return contactIds
      .map(id => contacts.find(c => c.id === id)?.name)
      .filter(Boolean)
      .join(', ');
  };

  const handleViewMessage = (historyItem) => {
    // Open modal or expand to show full message content
    alert(historyItem.content || 'No content available');
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl mx-4 h-[80vh] flex flex-col">
          <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
            <h2 className="text-lg font-semibold text-gray-900">Contact History</h2>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
              <FiX size={18} />
            </button>
          </div>
          
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <FiRefreshCw className="animate-spin mx-auto mb-4 text-gray-400" size={32} />
              <p className="text-gray-600">Loading contact history...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl mx-4 h-[80vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Contact History</h2>
            <p className="text-sm text-gray-600">
              Communication history for {contacts.length} contact{contacts.length > 1 ? 's' : ''}
            </p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <FiX size={18} />
          </button>
        </div>

        {/* Filters */}
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Filter by Contact
              </label>
              <select
                value={selectedContact}
                onChange={(e) => setSelectedContact(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              >
                <option value="all">All Contacts</option>
                {contacts.map(contact => (
                  <option key={contact.id} value={contact.id}>
                    {contact.name}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Filter by Type
              </label>
              <select
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              >
                <option value="all">All Types</option>
                <option value="email">Emails</option>
                <option value="linkedin">LinkedIn Messages</option>
              </select>
            </div>
            
            <div className="flex items-end">
              <button
                onClick={fetchHistory}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 flex items-center"
              >
                <FiRefreshCw className="mr-2" size={16} />
                Refresh
              </button>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mx-6 mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
            {error}
          </div>
        )}

        {/* History List */}
        <div className="flex-1 overflow-auto">
          {filteredHistory.length === 0 ? (
            <div className="p-12 text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                <FiMessageSquare className="text-gray-400" size={24} />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No communication history</h3>
              <p className="text-gray-600">
                No messages have been sent to these contacts yet.
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {filteredHistory.map((item) => (
                <div key={item.id} className="p-6 hover:bg-gray-50">
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0">
                      <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                        {getTypeIcon(item.type)}
                      </div>
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {getTypeLabel(item.type)}
                          </p>
                          <p className="text-sm text-gray-600">
                            To: {getContactNames(item.contactIds)}
                          </p>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className="text-xs text-gray-500">
                            {formatDate(item.sentAt)}
                          </span>
                          <button
                            onClick={() => handleViewMessage(item)}
                            className="p-1 text-gray-400 hover:text-blue-600"
                            title="View Message"
                          >
                            <FiEye size={14} />
                          </button>
                        </div>
                      </div>
                      
                      <div className="mt-2">
                        <p className="text-sm font-medium text-gray-900">
                          {item.subject || 'No subject'}
                        </p>
                        <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                          {item.preview || item.content || 'No content preview available'}
                        </p>
                      </div>
                      
                      <div className="mt-2 flex items-center space-x-4 text-xs text-gray-500">
                        <span>Sent by: {item.senderName}</span>
                        {item.status && (
                          <span className={`px-2 py-1 rounded-full ${
                            item.status === 'sent' 
                              ? 'bg-green-100 text-green-800'
                              : item.status === 'failed'
                              ? 'bg-red-100 text-red-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {item.status}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
          <div className="flex justify-between items-center text-sm text-gray-600">
            <span>
              Showing {filteredHistory.length} of {history.length} communications
            </span>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactHistory;